import {GoogleMap, MarkerF, useJsApiLoader} from '@react-google-maps/api';
import {useCallback, useEffect, useRef, useState} from 'react';
import {config} from '../config/config';
import {GeoPoint} from '../types';

interface GoogleMapPickerProps {
    value?: GeoPoint;
    onChange: (point: GeoPoint) => void;
    label?: string;
    // When provided, the map auto-centres + drops a pin from this address (debounced).
    geocodeQuery?: string;
    // When true, shows a "Use my location" button that centres on the device GPS.
    enableMyLocation?: boolean;
}

export default function GoogleMapPicker({value, onChange, label, geocodeQuery, enableMyLocation}: GoogleMapPickerProps) {
    const {isLoaded} = useJsApiLoader({
        id:               'google-map-script',
        googleMapsApiKey: config.googleMapsApiKey,
    });

    const geocoderRef = useRef<google.maps.Geocoder | null>(null);
    const [locating, setLocating] = useState(false);

    const [lng, lat] = (value?.coordinates || [0, 0]);
    const zoom = (Math.abs(lat) > 0 && Math.abs(lng) > 0 ? 15 : 4);

    const center = {
        lat: lat || 25.2048, // Default to Dubai
        lng: lng || 55.2708,
    };

    const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
            onChange({
                coordinates: [e.latLng.lng(), e.latLng.lat()],
                type:        'Point'
            });
        }
    }, [onChange]);

    // Auto-geocode the typed address (debounced) once the API is ready, so submitters
    // don't have to find the pin manually. They can still click/drag to fine-tune.
    useEffect(() => {
        if (!isLoaded || !geocodeQuery || geocodeQuery.trim().length < 4) return;
        const handle = setTimeout(() => {
            if (!geocoderRef.current) geocoderRef.current = new google.maps.Geocoder();
            geocoderRef.current.geocode({address: geocodeQuery}, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const loc = results[0].geometry.location;
                    onChange({coordinates: [loc.lng(), loc.lat()], type: 'Point'});
                }
            });
        }, 700);
        return () => clearTimeout(handle);
        // onChange omitted on purpose — only the address query should trigger a re-geocode.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, geocodeQuery]);

    const useMyLocation = () => {
        if (!navigator.geolocation) return;
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                onChange({coordinates: [pos.coords.longitude, pos.coords.latitude], type: 'Point'});
                setLocating(false);
            },
            () => setLocating(false),
            {enableHighAccuracy: true, timeout: 10000}
        );
    };

    return (
        <>
            {label && <div className="mt-0!">{label}</div>}
            {enableMyLocation && (
                <button
                    type="button"
                    onClick={useMyLocation}
                    disabled={locating}
                    className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-default-300 px-3 py-1 text-sm font-medium hover:bg-default-100 disabled:opacity-50"
                >
                    <span aria-hidden>📍</span> {locating ? 'Locating…' : 'Use my location'}
                </button>
            )}
            <div className="h-64 mt-0!">
                {isLoaded ? (
                    <GoogleMap
                        mapContainerStyle={{width: '100%', height: '100%'}}
                        center={center}
                        zoom={zoom}
                        onClick={handleMapClick}
                        options={{
                            disableDefaultUI:  true,
                            zoomControl:       true,
                            scrollwheel:       true,
                            mapTypeControl:    false,
                            streetViewControl: false,
                            fullscreenControl: false,
                        }}
                    >
                        {(lat !== 0 || lng !== 0) && <MarkerF position={center} />}
                    </GoogleMap>
                ) : (
                    <div className="h-full w-full flex items-center justify-center bg-default-100">
                        <span className="text-default-400">Loading map...</span>
                    </div>
                )}
            </div>
        </>
    );
}
